import { Modal, Row, Select, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getTeamInfo, updateSessionDurationSource } from "@store/actions/team.actions";
import { audioMessage } from "@components/Common/Notifications/audioMessage";
import { PermissionOverlay } from "@components/PermissionedComponents";

const { Text } = Typography;

const MAX = "max";

interface IDurationProvider {
    key: string;
    name: string;
}

// A team with two or more GPS providers chooses which value sets the session duration.
export const SessionDurationSource = () => {
    const dispatch = useDispatch();
    const {
        session_duration_options: providers = [],
        session_duration_source_effective: effective,
    } = useSelector(({ team }: any) => team.info);

    if (providers.length < 2) return null;

    const names = providers.map((p: IDurationProvider) => p.name);
    const nameOf = (key: string) => providers.find((p: IDurationProvider) => p.key === key)?.name ?? key;
    const options = [
        ...providers.map((p: IDurationProvider) => ({ value: p.key, label: p.name })),
        { value: MAX, label: `Higher value (${names.join(" or ")})` },
    ];

    let help: string;
    if (!effective) {
        help = "The GPS unit matches no provider. The last import sets the session duration.";
    } else if (effective === MAX) {
        help = `Each import keeps the higher of the stored duration and the new ${names.join(" or ")} duration.`;
    } else {
        help = `${nameOf(effective)} sets the session duration. Another provider fills it only when ${nameOf(effective)} has no duration.`;
    }

    const onChange = (value: string) => {
        Modal.confirm({
            title: "Change the session duration source?",
            content: "The new setting applies to the next imports. Import a date again to update its session duration.",
            okText: "Change",
            onOk: async () => {
                try {
                    await dispatch(updateSessionDurationSource(value) as any);
                    audioMessage.success("Session duration source updated");
                } catch (err: any) {
                    audioMessage.error(typeof err === "string" ? err : "Couldn't change the session duration source.");
                    return;
                }
                try {
                    await dispatch(getTeamInfo() as any);
                } catch {
                    audioMessage.warning("Saved. Reload the page to see the new value.");
                }
            },
        });
    };

    return (
        <div style={{ marginTop: 16, padding: "12px 16px", border: "1px solid #f0f0f0", borderRadius: 6 }}>
            <Row align="middle" style={{ gap: 12 }}>
                <Text strong>Session duration</Text>
                <PermissionOverlay
                    permissionCheck="EditGeneralTeamSettings"
                    tooltipMessage="You don't have permission to edit general team settings"
                >
                    <Select
                        style={{ width: 280 }}
                        // The backend resolves a null or removed saved value to the GPS unit's provider.
                        value={effective ?? undefined}
                        placeholder="Choose a source"
                        onChange={onChange}
                        options={options}
                    />
                </PermissionOverlay>
            </Row>
            <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{help}</Text>
            </div>
        </div>
    );
};
